import type { AxiosError as ApiError } from "axios"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import {
  type BodyLoginLoginAccessToken as AccessToken,
  Login,
  type UserDetails,
  type UserRegister,
  Users,
} from "@/client"
import { handleError } from "@/utils"
import { clearAuthState, getTokenClaims, isAuthenticated, setStoredAccessToken } from "@/utils/auth"
import { UserRole, Permission, hasPermission, hasAnyPermission, hasAllPermissions, hasHigherOrEqualRole, resolveUserRole } from "@/types/permissions"

const isLoggedIn = () => {
  return isAuthenticated()
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const { data: user } = useQuery<UserDetails | null, Error>({
    queryKey: ["currentUser"],
    queryFn: async () => {
      try {
        return await Users.readUserMe()
      } catch (err: any) {
        // If we get a 401 or 404, the token is invalid
        if (err?.status === 401 || err?.status === 404) {
          clearAuthState()
          return null
        }
        throw err
      }
    },
    enabled: isLoggedIn(),
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.status === 401 || error?.status === 404) {
        return false
      }
      return failureCount < 3
    },
  })

  const tokenClaims = getTokenClaims()

  const signUpMutation = useMutation({
    mutationFn: (data: UserRegister) =>
      Users.registerUser({ body: data }),

    onSuccess: () => {
      navigate({ to: "/login" })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const login = async (data: AccessToken) => {
    const response = await Login.loginAccessToken({
      body: data,
    })

    setStoredAccessToken(response.access_token)

    // Refresh auth-dependent data after token changes.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["currentUser"] }),
      queryClient.invalidateQueries({ queryKey: ["authContext"] }),
    ])
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      console.log("Login successful")
      navigate({ to: "/" })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const logout = () => {
    clearAuthState()
    queryClient.clear()
    navigate({ to: "/login" })
  }

  // Role and permission helpers
  const userRole = resolveUserRole({
    role: user?.role,
    roles: tokenClaims?.roles,
  }) as UserRole | undefined
  
  const checkPermission = (permission: Permission): boolean => {
    return hasPermission(userRole, permission)
  }
  
  const checkAnyPermission = (permissions: Permission[]): boolean => {
    return hasAnyPermission(userRole, permissions)
  }
  
  const checkAllPermissions = (permissions: Permission[]): boolean => {
    return hasAllPermissions(userRole, permissions)
  }
  
  const checkRole = (requiredRole: UserRole): boolean => {
    return hasHigherOrEqualRole(userRole, requiredRole)
  }

  return {
    signUpMutation,
    loginMutation,
    logout,
    user,
    userRole,
    isLoggedIn: isLoggedIn(),
    error,
    resetError: () => setError(null),
    // Permission checking methods
    checkPermission,
    checkAnyPermission, 
    checkAllPermissions,
    checkRole,
  }
}

export { isLoggedIn }
export default useAuth
